import type {
  AudioLanguage,
  ContentRating,
  Episode,
  Game,
  Genre,
  Product,
  Show,
  UserPreferences,
} from '@gobingebox/shared';
import { apiFetch } from '@/lib/api';

export interface BrowseRow {
  key: string;
  title: string;
  shows: Show[];
}
export interface BrowseResponse {
  featured: Show[];
  rows: BrowseRow[];
}
export interface ShowResponse {
  show: Show;
  episodes: Episode[];
}
export interface ContinueItem {
  show: Show;
  episode: Episode;
  progressSeconds: number;
}
export interface PlaybackInfo {
  mode: 'preview' | 'full';
  status: 'ready' | 'locked' | 'unavailable' | 'upcoming' | 'age_restricted';
  reason?: string;
  releaseAt?: string;
  requiredAge?: number;
  rating?: ContentRating | null;
  provider?: 'cloudflare';
  src?: string;
  signed?: boolean;
  audioLanguages: AudioLanguage[];
}

const post = (data: unknown): RequestInit => ({ method: 'POST', body: JSON.stringify(data) });

export const catalogApi = {
  browse: () => apiFetch<BrowseResponse>('/catalog/browse'),
  genres: () => apiFetch<{ genres: Genre[] }>('/catalog/genres'),
  show: (slug: string) => apiFetch<ShowResponse>(`/catalog/shows/${slug}`),
  search: (q: string) => apiFetch<{ shows: Show[]; query: string }>(`/catalog/search?q=${encodeURIComponent(q)}`),
  playback: (episodeId: string, mode: 'preview' | 'full') =>
    apiFetch<PlaybackInfo>(`/catalog/episodes/${episodeId}/playback?mode=${mode}`),
  games: () => apiFetch<{ games: Game[] }>('/games'),
  storeProducts: () => apiFetch<{ products: Product[] }>('/store'),
  product: (slug: string) => apiFetch<{ product: Product }>(`/store/${slug}`),
};

export const meApi = {
  list: () => apiFetch<{ shows: Show[] }>('/me/list'),
  addToList: (showId: string) => apiFetch<{ ok: boolean }>('/me/list', post({ showId })),
  removeFromList: (showId: string) =>
    apiFetch<{ ok: boolean }>(`/me/list/${showId}`, { method: 'DELETE' }),
  continueWatching: () => apiFetch<{ items: ContinueItem[] }>('/me/continue'),
  getProgress: (episodeId: string) =>
    apiFetch<{ progressSeconds: number }>(`/me/watch/${episodeId}`),
  setProgress: (episodeId: string, progressSeconds: number) =>
    apiFetch<{ ok: boolean }>(`/me/watch/${episodeId}`, {
      method: 'PUT',
      body: JSON.stringify({ progressSeconds }),
    }),
  updateProfile: (displayName: string) =>
    apiFetch<{ ok: boolean }>('/me/profile', { method: 'PATCH', body: JSON.stringify({ displayName }) }),
  updatePreferences: (prefs: Partial<UserPreferences>) =>
    apiFetch<{ ok: boolean; preferences: UserPreferences }>('/me/preferences', {
      method: 'PUT',
      body: JSON.stringify(prefs),
    }),
};

export const playbackApi = {
  startSession: (episodeId: string) =>
    apiFetch<{ sessionId: string; limit: number; active: number }>(
      '/playback/session',
      post({ episodeId }),
    ),
  heartbeat: (id: string) =>
    apiFetch<{ ok: boolean }>(`/playback/session/${id}/heartbeat`, post({})),
  stopSession: (id: string) =>
    apiFetch<{ ok: boolean }>(`/playback/session/${id}`, { method: 'DELETE' }),
};
